export type PrecisionAmount = {
	value: number
	precision: number
}
export type TaxAmount = {
	gross: PrecisionAmount
	net: PrecisionAmount
	tax: PrecisionAmount
}

// JsonValue is used to allow for the use of Prisma's Json type
export type AmountInput = number | PrecisionAmount

export const isPrecisionAmount = (value: any): value is PrecisionAmount => {
	return (
		typeof value === 'object' &&
		!!value &&
		'value' in value &&
		typeof value.value === 'number' &&
		!isNaN(value.value) &&
		'precision' in value &&
		typeof value.precision === 'number' &&
		!isNaN(value.precision)
	)
}

export const isTaxAmount = (value: any): value is TaxAmount => {
	return typeof value === 'object' && !!value && 'gross' in value && 'net' in value && 'tax' in value
}

export const toPrecision = (value: AmountInput, precisionToConvertTo = 2): PrecisionAmount => {
	if (typeof value === 'number') {
		return convertToPrecision(
			{
				value,
				precision: 0
			},
			precisionToConvertTo
		)
	} else if (!isPrecisionAmount(value)) {
		throw new Error(`Invalid amount input: ${JSON.stringify(value)}`)
	}
	return value
}
export const convertToPrecision = (value: PrecisionAmount, precision = 2): PrecisionAmount => {
	if (value.precision === precision) {
		return value
	}

	return {
		value: Math.round(value.value * Math.pow(10, precision - value.precision)),
		precision
	}
}
export const fromPrecision = (input: AmountInput) => {
	if (typeof input === 'number') {
		return input
	}
	const precisionAmount = toPrecision(input)
	return precisionAmount.value / Math.pow(10, precisionAmount.precision)
}

export const add = (a: AmountInput | TaxAmount, b: AmountInput | TaxAmount) => {
	if (isTaxAmount(a) || isTaxAmount(b)) {
		if (isTaxAmount(a) && isTaxAmount(b)) {
			return {
				gross: add(a.gross, b.gross),
				net: add(a.net, b.net),
				tax: add(a.tax, b.tax)
			}
		}
		throw new Error(`Cannot add tax amount to non-tax amount: ${JSON.stringify(a)} + ${JSON.stringify(b)}`)
	}

	const aPrecisionAmount = toPrecision(a)
	const bPrecisionAmount = toPrecision(b)

	const precision = Math.max(aPrecisionAmount.precision, bPrecisionAmount.precision)
	const aConverted = convertToPrecision(aPrecisionAmount, precision)
	const bConverted = convertToPrecision(bPrecisionAmount, precision)

	return {
		value: Math.round(aConverted.value + bConverted.value),
		precision
	}
}

export const subtract = (a: AmountInput | TaxAmount, b: AmountInput | TaxAmount) => {
	if (isTaxAmount(a) || isTaxAmount(b)) {
		if (isTaxAmount(a) && isTaxAmount(b)) {
			return {
				gross: subtract(a.gross, b.gross),
				net: subtract(a.net, b.net),
				tax: subtract(a.tax, b.tax)
			}
		}
		throw new Error(`Cannot add tax amount to non-tax amount: ${JSON.stringify(a)} + ${JSON.stringify(b)}`)
	}

	const aPrecisionAmount = toPrecision(a)
	const bPrecisionAmount = toPrecision(b)

	const precision = Math.max(aPrecisionAmount.precision, bPrecisionAmount.precision)
	const aConverted = convertToPrecision(aPrecisionAmount, precision)
	const bConverted = convertToPrecision(bPrecisionAmount, precision)

	return {
		value: Math.round(aConverted.value - bConverted.value),
		precision
	}
}

export const multiply = (a: AmountInput | TaxAmount, b: AmountInput | TaxAmount) => {
	if (isTaxAmount(a) || isTaxAmount(b)) {
		if (isTaxAmount(a) && isTaxAmount(b)) {
			throw new Error(`Cannot divide tax amount by other tax amount: ${JSON.stringify(a)} / ${JSON.stringify(b)}`)
		}
		const taxAmount = (isTaxAmount(a) ? a : b) as TaxAmount
		const nonTaxAmount = (isTaxAmount(a) ? b : a) as AmountInput
		return {
			gross: multiply(taxAmount.gross, nonTaxAmount),
			net: multiply(taxAmount.net, nonTaxAmount),
			tax: multiply(taxAmount.tax, nonTaxAmount)
		}
	}

	let aPrecisionAmount = toPrecision(a)
	let bPrecisionAmount = toPrecision(b)

	const outputPrecision = Math.max(aPrecisionAmount.precision, bPrecisionAmount.precision)

	aPrecisionAmount = convertToPrecision(aPrecisionAmount, outputPrecision)
	bPrecisionAmount = convertToPrecision(bPrecisionAmount, outputPrecision)

	return convertToPrecision(
		{
			value: Math.round(aPrecisionAmount.value * bPrecisionAmount.value),
			precision: aPrecisionAmount.precision + bPrecisionAmount.precision
		},
		outputPrecision
	)
}

export const divide = (a: AmountInput | TaxAmount, b: AmountInput | TaxAmount) => {
	if (isTaxAmount(a) || isTaxAmount(b)) {
		if (isTaxAmount(a) && isTaxAmount(b)) {
			throw new Error(`Cannot divide tax amount by other tax amount: ${JSON.stringify(a)} / ${JSON.stringify(b)}`)
		}
		const taxAmount = (isTaxAmount(a) ? a : b) as TaxAmount
		const nonTaxAmount = (isTaxAmount(a) ? b : a) as AmountInput
		return {
			gross: divide(taxAmount.gross, nonTaxAmount),
			net: divide(taxAmount.net, nonTaxAmount),
			tax: divide(taxAmount.tax, nonTaxAmount)
		}
	}

	let aPrecisionAmount = toPrecision(a)
	const bPrecisionAmount = toPrecision(b)

	const outputPrecision = Math.max(aPrecisionAmount.precision, bPrecisionAmount.precision)

	aPrecisionAmount = convertToPrecision(aPrecisionAmount, aPrecisionAmount.precision + bPrecisionAmount.precision)
	return convertToPrecision(
		{
			value: Math.round(aPrecisionAmount.value / bPrecisionAmount.value),
			precision: aPrecisionAmount.precision - bPrecisionAmount.precision
		},
		outputPrecision
	)
}
export const sum = (values: AmountInput[]) => {
	return values.reduce<PrecisionAmount>((acc, next) => add(acc, next), toPrecision(0))
}

export const sumTaxAmounts = (values: TaxAmount[]) => {
	return values.reduce<TaxAmount>((acc, next) => add(acc, next), {
		gross: toPrecision(0),
		net: toPrecision(0),
		tax: toPrecision(0)
	})
}

export const compare = (a: AmountInput, b: AmountInput) => {
	let aPrecisionAmount = toPrecision(a)
	let bPrecisionAmount = toPrecision(b)
	const outputPrecision = Math.max(aPrecisionAmount.precision, bPrecisionAmount.precision)

	aPrecisionAmount = convertToPrecision(aPrecisionAmount, outputPrecision)
	bPrecisionAmount = convertToPrecision(bPrecisionAmount, outputPrecision)

	return aPrecisionAmount.value - bPrecisionAmount.value
}

export const largerThan = (a: AmountInput, b: AmountInput) => {
	return compare(a, b) > 0
}

export const isEqualTo = (a: AmountInput, b: AmountInput) => {
	return compare(a, b) === 0
}

export const smallerThan = (a: AmountInput, b: AmountInput) => {
	return compare(a, b) < 0
}

export const roundCurrencyToNearestDecimals = (
	amount: AmountInput | TaxAmount,
	currency: string,
	overrides: Record<string, number> = {}
) => {
	if (isTaxAmount(amount)) {
		return {
			gross: roundCurrencyToNearestDecimals(amount.gross, currency, overrides),
			net: roundCurrencyToNearestDecimals(amount.net, currency, overrides),
			tax: roundCurrencyToNearestDecimals(amount.tax, currency, overrides)
		}
	}

	let decimals = 0
	if (overrides[currency]) {
		decimals = overrides[currency]
	} else {
		decimals = getCurrencyDecimals(currency)
	}
	const convertedAmount = toPrecision(amount)
	return convertToPrecision(
		{
			value: Math.round(fromPrecision(convertedAmount) * Math.pow(10, decimals)),
			precision: decimals
		},
		decimals
	)
}

export const computeTax = (
	price: PrecisionAmount,
	taxInclusive: boolean,
	taxRate: PrecisionAmount,
	precisionToConvertTo = 2
): TaxAmount => {
	let gross: PrecisionAmount
	let tax: PrecisionAmount
	let net: PrecisionAmount
	if (taxInclusive) {
		gross = toPrecision(price, precisionToConvertTo)
		net = divide(gross, add(1, taxRate))
		tax = subtract(gross, net)
	} else {
		net = toPrecision(price, precisionToConvertTo)
		tax = multiply(toPrecision(price, precisionToConvertTo), toPrecision(taxRate, precisionToConvertTo))
		gross = add(net, tax)
	}
	return { gross, tax, net }
}

type OperationType = 'add' | 'subtract' | 'multiply' | 'divide'
type Operation = {
	type: OperationType
	amount: AmountInput
}

export const amount = (input: AmountInput) => new Amount(input)
export class Amount {
	private value: PrecisionAmount
	private operations: Operation[] = []
	constructor(input: AmountInput) {
		this.value = toPrecision(input)
	}

	toNumber() {
		return fromPrecision(this.value)
	}

	add(other: AmountInput) {
		const aPrecisionAmount = toPrecision(this.value)
		const bPrecisionAmount = toPrecision(other)

		const precision = Math.max(aPrecisionAmount.precision, bPrecisionAmount.precision)
		const aConverted = convertToPrecision(aPrecisionAmount, precision)
		const bConverted = convertToPrecision(bPrecisionAmount, precision)

		return {
			value: Math.round(aConverted.value + bConverted.value),
			precision
		}
	}

	subtract(other: Amount) {}
}
export const getCurrencyDecimals = (currency: string) => {
	const formatter = new Intl.NumberFormat([], {
		style: 'currency',
		currency
	})
	const decimals = formatter.resolvedOptions().minimumFractionDigits
	if (decimals === undefined) {
		throw new Error(`Unable to determine number of decimals for currency: ${currency}`)
	}
	return decimals
}
